import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import {
  People,
  VerifiedUser,
  Diamond,
  Assessment,
} from '@mui/icons-material';
import { Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import adminService from '../../services/adminService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const StatCard = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {React.cloneElement(icon, { sx: { color } })}
        <Typography variant="h6" sx={{ ml: 1, color }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ color }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await adminService.getDashboardStats();
      if (response.data.success) {
        setStats(response.data.data);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setError('Failed to load dashboard statistics. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // User activity chart data
  const userActivityData = {
    labels: stats?.userActivity?.labels || [],
    datasets: [
      {
        label: 'New Users',
        data: stats?.userActivity?.data || [],
        backgroundColor: 'rgba(37, 99, 235, 0.18)',
        fill: false,
        borderColor: '#2563eb',
        tension: 0.1,
      },
    ],
  };

  // Gender distribution chart data
  const genderData = {
    labels: stats?.genderDistribution?.map(item => item._id) || [],
    datasets: [
      {
        data: stats?.genderDistribution?.map(item => item.count) || [],
        backgroundColor: ['#2563eb', '#0f766e', '#b45309'],
      },
    ],
  };

  // Membership distribution chart data
  const membershipData = {
    labels: stats?.membershipDistribution?.map(item => item._id) || [],
    datasets: [
      {
        data: stats?.membershipDistribution?.map(item => item.count) || [],
        backgroundColor: ['#1e3a8a', '#1d4ed8', '#2dd4bf'],
      },
    ],
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* Total Users */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats?.totalUsers || 0}
            icon={<People />}
            color="primary.main"
          />
        </Grid>

        {/* Verified Profiles */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Verified Profiles"
            value={stats?.verifiedProfiles || 0}
            icon={<VerifiedUser />}
            color="success.main"
          />
        </Grid>

        {/* Premium Members */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Premium Members"
            value={stats?.premiumMembers || 0}
            icon={<Diamond />}
            color="secondary.main"
          />
        </Grid>

        {/* Pending Verifications */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Verifications"
            value={stats?.pendingVerifications || 0}
            icon={<Assessment />}
            color="warning.main"
          />
        </Grid>

        {/* User Activity Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              User Activity
            </Typography>
            <Line
              data={userActivityData}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: true,
                    text: 'Monthly New Users',
                  },
                },
              }}
            />
          </Paper>
        </Grid>

        {/* Distribution Charts */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Gender Distribution
                </Typography>
                <Box sx={{ height: 200 }}>
                  <Pie data={genderData} />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Membership Distribution
                </Typography>
                <Box sx={{ height: 200 }}>
                  <Pie data={membershipData} />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            {stats?.recentActivity?.length > 0 ? (
              stats.recentActivity.map((activity, index) => (
                <Box
                  key={index}
                  sx={{
                    py: 1,
                    borderBottom:
                      index < stats.recentActivity.length - 1
                        ? '1px solid #eee'
                        : 'none',
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    {new Date(activity.timestamp).toLocaleString()}
                  </Typography>
                  <Typography>{activity.description}</Typography>
                </Box>
              ))
            ) : (
              <Typography color="textSecondary">No recent activity</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 







